import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, KeyRound, X } from "lucide-react";

interface ProviderStatus {
  groq_configured: boolean;
  gemini_configured: boolean;
  local_configured: boolean;
  local_api_url: string;
  local_model: string;
}

export function ProviderSettingsModal({ onClose }: { onClose: () => void }) {
  const [status, setStatus] = useState<ProviderStatus>({ groq_configured: false, gemini_configured: false, local_configured: false, local_api_url: "", local_model: "" });
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    void api("/settings/providers")
      .then((data) => {
        const next = data as ProviderStatus;
        setStatus(next);
      })
      .catch(() => setFeedback("Unable to read provider status."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby="provider-settings-title" className="w-full max-w-lg rounded-t-2xl border bg-card p-4 shadow-2xl sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="provider-settings-title" className="flex items-center gap-2 font-semibold"><KeyRound size={18} className="text-primary" />Provider API keys</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Provider configuration is managed through Worker environment variables.</p>
          </div>
          <Button aria-label="Close settings" variant="ghost" size="icon" onClick={onClose}><X /></Button>
        </div>

        <div className="mt-5 space-y-4">
          <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">LM Studio (primary)</div>
                <div className="mt-1 text-[11px] text-muted-foreground">OpenAI-compatible API. Called first before Groq and Gemini.</div>
              </div>
              {status.local_configured && <span className="flex items-center gap-1 text-[11px] text-emerald-400"><CheckCircle2 size={12} />Configured</span>}
            </div>
            <div className="mt-3 grid gap-2 text-xs text-muted-foreground">
              <div>Endpoint: <span className="text-foreground">{loading ? "Checking..." : status.local_api_url || "Not configured"}</span></div>
              <div>Model: <span className="text-foreground">{loading ? "Checking..." : status.local_model || "Not configured"}</span></div>
            </div>
          </div>
          <div className="grid gap-3 text-sm">
            {["Groq", "Gemini"].map((provider) => <div key={provider} className="flex items-center justify-between rounded-lg border px-3 py-2"><span>{provider}</span><span className="flex items-center gap-1 text-xs text-emerald-400">{(provider === "Groq" ? status.groq_configured : status.gemini_configured) && <CheckCircle2 size={12} />} {(provider === "Groq" ? status.groq_configured : status.gemini_configured) ? "Configured" : "Not configured"}</span></div>)}
          </div>
        </div>

        {feedback && <p className="mt-4 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{feedback}</p>}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
